import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Doctor, PortalMode } from '../../types/api';
import { apiService } from '../../services/api';
import { mapStoredUserToDoctor, parseStoredUser } from '../../utils/doctorUser';
import { getApiErrorMessage } from '../../utils/apiError';
import { inferPortalFromUser } from '../../utils/teleSession';
import { AspenHealthLogo } from '../../components/AspenHealthLogo';
import SingpassLoginButton from '../../components/SingpassLoginButton';
import { startHqSso } from '../../services/hqSso';
import { singpassLoginError, singpassStaffNotice } from '../../utils/singpassLoginError';

interface LoginPageProps {
  onLogin: (doctor: Doctor, needsSignature: boolean, portal?: PortalMode) => void;
}

function LoginPage({ onLogin }: LoginPageProps) {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string>('');
  const [singpassLoading, setSingpassLoading] = useState<boolean>(false);
  const [hqLoading, setHqLoading] = useState<boolean>(false);
  const singpassResumeHandled = useRef(false);
  const singpassCode = singpassLoginError(searchParams.get('singpass'));
  const allowHq = singpassCode == null || singpassCode === 'unavailable';

  useEffect(() => {
    if (singpassCode) {
      setError(singpassStaffNotice(singpassCode, true));
      return;
    }
    const sso = searchParams.get('sso');
    if (sso !== 'error' && sso !== 'used') return;
    setError(searchParams.get('msg') || 'HQ sign-in was not accepted. Please try again.');
  }, [searchParams, singpassCode]);

  useEffect(() => {
    if (singpassResumeHandled.current) return;
    if (sessionStorage.getItem('drTele_postSingpassDoLogin') !== '1') return;
    singpassResumeHandled.current = true;
    sessionStorage.removeItem('drTele_postSingpassDoLogin');
    const u = parseStoredUser();
    if (u && localStorage.getItem('accessToken')) {
      const portal = inferPortalFromUser(u);
      onLogin(mapStoredUserToDoctor(u), portal === 'doctor', portal);
    }
  }, [onLogin]);

  const handleSingpass = async () => {
    setError('');
    setSingpassLoading(true);
    try {
      const authUrl = await apiService.getDoctorSingpassAuthUrl();
      window.location.href = authUrl;
    } catch (err: unknown) {
      console.error('Singpass login error:', err);
      setError(getApiErrorMessage(err, 'Unable to start Singpass login. Please try again.'));
      setSingpassLoading(false);
    }
  };

  const handleHqSso = async () => {
    setError('');
    setHqLoading(true);
    try {
      await startHqSso({ prompt: 'login' });
    } catch (err: unknown) {
      console.error('HQ SSO login error:', err);
      setError(getApiErrorMessage(err, 'Unable to start HQ sign-in. Please try again.'));
      setHqLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <AspenHealthLogo variant="login" showPortalLabel />
        <p className="login-subtitle">
          Sign in with Singpass, or with HQ if you already use that for the management apps. Doctors open the consult queue. Other staff open the waiting-room chat.
        </p>
        {error && (
          <div style={{ color: '#f87171', marginBottom: 16, fontSize: 14 }}>{error}</div>
        )}
        <SingpassLoginButton
          variant="white"
          disabled={singpassLoading || hqLoading}
          loading={singpassLoading}
          loadingLabel="Redirecting to Singpass..."
          onClick={() => void handleSingpass()}
        />
        {allowHq && (
        <button
          type="button"
          disabled={singpassLoading || hqLoading}
          onClick={() => void handleHqSso()}
          style={{
            marginTop: 12,
            width: '100%',
            minHeight: 48,
            borderRadius: 8,
            border: '1px solid #cbd5e1',
            background: '#fff',
            color: '#0f172a',
            fontSize: 16,
            fontWeight: 600,
            cursor: singpassLoading || hqLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {hqLoading ? 'Redirecting to HQ...' : 'Sign in with HQ'}
        </button>
        )}
      </div>
    </div>
  );
}

export default LoginPage;
